<template>
  <div>
    <CRow>
      <CCol sm="12">
        <CCard>
          <CCardBody class="p-4">
            <CButton color="link" class="px-0 mb-2" @click="cancel">
              <CIcon name="cil-arrow-left" /> Chart of accounts
            </CButton>
            <h1 class="h4 mb-1 font-weight-bold">
              {{ obj.id ? obj.name || "Account" : "Add account" }}
            </h1>
            <p class="text-muted mb-4">
              Name the account and choose where it sits in your chart of accounts
            </p>
            <CForm>
              <CInput label="Name" horizontal v-model="obj.name" />
              <CInput
                label="Category"
                horizontal
                readonly
                :value.sync="selectedCategory.name"
              >
                <template #append>
                  <CButton color="primary" @click="onSearchCategory()">
                    Choose
                  </CButton>
                </template>
              </CInput>
              <CSelect
                horizontal
                :value.sync="selectedAccountType"
                :options="accountTypes"
                label="Account type"
              />

              <CInput label="Account no." horizontal v-model="obj.accountNo" />
            </CForm>
          </CCardBody>
          <CCardFooter class="d-flex justify-content-between">
            <CButton color="light" :disabled="saving" @click="cancel">
              Cancel
            </CButton>
            <CButton color="primary" :disabled="saving" @click="submit">
              <CSpinner v-if="saving" size="sm" class="mr-1" />
              {{ obj.id ? "Save" : "Add account" }}
            </CButton>
          </CCardFooter>
        </CCard>
      </CCol>
    </CRow>
    <CRow v-if="obj.id">
      <CCol>
        <CCard>
          <CCardBody class="p-4">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h2 class="h6 font-weight-bold mb-1">Merchant keywords</h2>
                <p class="small text-muted mb-0">
                  Bank transactions whose description contains one of these words
                  are matched to this account
                </p>
              </div>
              <CButton color="primary" variant="outline" @click="addNewMerchant">
                <CIcon name="cil-plus" /> Add keyword
              </CButton>
            </div>
            <div v-if="merchantLoading && !merchantItems.length" class="text-center p-4">
              <CSpinner color="primary" />
            </div>
            <div v-else-if="merchantError && !merchantItems.length" class="text-center p-4">
              <p class="text-muted">
                Couldn't load the keywords. Check your connection and try again.
              </p>
              <CButton color="primary" @click="refreshMerchants">
                <CIcon name="cil-reload" /> Retry
              </CButton>
            </div>
            <div v-else-if="!merchantItems.length" class="text-center p-4 text-muted">
              No keywords yet. Add one to match bank transactions automatically.
            </div>
            <ul v-else class="list-unstyled mb-0">
              <li
                v-for="item in merchantItems"
                :key="item.id"
                class="d-flex justify-content-between align-items-center border-bottom py-2"
              >
                <span>{{ item.name }}</span>
                <CButton
                  color="light"
                  :aria-label="`Delete keyword ${item.name}`"
                  @click="showDeleteConfirmation(item)"
                >
                  <CIcon name="cil-trash" class="text-danger" />
                </CButton>
              </li>
            </ul>
          </CCardBody>
        </CCard>
        <CModal
          :show.sync="editMerchantModal"
          :close-on-backdrop="false"
          centered
          title="Add keyword"
        >
          <CForm @submit.prevent="updateMerchant">
            <CInput label="Keyword *" v-model="merchant.name" />
          </CForm>
          <template #footer>
            <CButton
              color="light"
              :disabled="savingMerchant"
              @click="editMerchantModal = false"
            >
              Cancel
            </CButton>
            <CButton
              color="primary"
              :disabled="savingMerchant || !(merchant.name || '').trim()"
              @click="updateMerchant"
            >
              {{ merchant.id ? "Save" : "Add keyword" }}
            </CButton>
          </template>
        </CModal>

        <CModal :show.sync="warningModal" :close-on-backdrop="!deleting" centered>
          <template #header>
            <h5 class="modal-title">Delete keyword “{{ itemToDelete.name }}”?</h5>
          </template>
          <p class="mb-0">
            Bank transactions will no longer be matched to this account by this
            keyword.
          </p>
          <template #footer>
            <CButton color="light" :disabled="deleting" @click="warningModal = false">
              Keep it
            </CButton>
            <CButton color="danger" :disabled="deleting" @click="doDeleteMerchant">
              Delete keyword
            </CButton>
          </template>
        </CModal>
      </CCol>
    </CRow>
    <CModal
      title="Choose a category"
      :show.sync="categorySearchPopup"
      size="lg"
      centered
    >
      <CDataTable
        :items="categoryList"
        :fields="categoryFieldList"
        column-filter
        sorter
        pagination
        :loading="loading"
      >
        <template #show_details="{ item }">
          <td class="py-2">
            <CButton
              color="primary"
              variant="outline"
              size="sm"
              @click="onCategorySelected(item)"
            >
              Select
            </CButton>
          </td>
        </template>
      </CDataTable>
      <template #footer>
        <CButton color="light" @click="categorySearchPopup = false">Cancel</CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import ChartOfAccountApi from "@/lib/chartOfAccountApi";
import MerchantApi from "@/lib/merchantApi";

export default {
  name: "ChartOfAccount",
  data: () => {
    return {
      categorySearchPopup: false,
      categoryList: [],
      categoryFieldList: [
        { key: "name", label: "Name" },
        {
          key: "show_details",
          label: "",
          _style: "width:1%",
          sorter: false,
          filter: false,
        },
      ],
      selectedCategory: {
        name: "",
      },
      accountTypes: [],
      selectedAccountType: null,

      editMerchantModal: false,
      merchant: {},
      warningModal: false,
      itemToDelete: {},
      merchantItems: [],
      merchantLoading: false,
      merchantError: false,
      savingMerchant: false,
      deleting: false,
      saving: false,
      loading: false,
      obj: {},
      submitted: false,
      api: new ChartOfAccountApi(),
      merchantApi: new MerchantApi(),
    };
  },
  mounted() {
    this.fetchAccountTypes();
    this.resetObj();
  },
  methods: {
    onCategorySelected(item) {
      this.selectedCategory = item;
      this.obj.chartAccountGroupId = item.id;
      this.categorySearchPopup = false;
    },

    onSearchCategory() {
      this.refreshTableCategory();
      this.categorySearchPopup = true;
    },
    refreshTableCategory() {
      this.loading = true;
      this.api
        .getCategoryListByCurrentBusiness()
        .then((response) => {
          this.categoryList = response.result;
        })
        .catch((err) => {
          console.error(err);
          this.$toast.error(
            "Couldn't load the categories. Check your connection and try again."
          );
        })
        .finally(() => {
          this.loading = false;
        });
    },

    cancel() {
      this.$router.push({ path: "/tenants/chartOfAccountList" });
    },
    fetchAccountTypes() {
      this.api
        .getAccountTypes()
        .then((response) => {
          var obj = response.result;
          this.accountTypes = obj.map((state) => ({
            value: state.id,
            label: state.name,
          }));
          if (this.selectedAccountType === null) this.selectedAccountType = 0;
        })
        .catch((err) => {
          console.error(err);
          this.$toast.error(
            "Couldn't load the account types. Reload the page to try again."
          );
        });
    },
    updateMerchant() {
      if (!(this.merchant.name || "").trim()) return;
      this.merchant.businessId = this.obj.businessId;
      this.merchant.chartAccountId = this.obj.id;
      this.addOrUpdateSubscription(this.merchant);
    },
    addOrUpdateSubscription(editObj) {
      this.savingMerchant = true;
      const req = editObj.id
        ? this.merchantApi.update(editObj)
        : this.merchantApi.create(editObj);
      req
        .then(() => {
          this.$toast.success(editObj.id ? "Keyword saved" : "Keyword added");
          this.refreshMerchants();
          this.editMerchantModal = false;
        })
        .catch(({ data } = {}) => {
          this.$toast.error(
            `Couldn't save the keyword. ${helper.getErrorMessage(data)}`
          );
        })
        .finally(() => {
          this.savingMerchant = false;
        });
    },
    doDeleteMerchant() {
      this.deleting = true;
      this.merchantApi
        .delete(this.itemToDelete.id)
        .then(() => {
          this.$toast.success("Keyword deleted");
          this.warningModal = false;
          this.itemToDelete = {};
          this.refreshMerchants();
        })
        .catch(({ data } = {}) => {
          this.$toast.error(
            `Couldn't delete the keyword. ${helper.getErrorMessage(data)}`
          );
        })
        .finally(() => {
          this.deleting = false;
        });
    },
    showDeleteConfirmation(item) {
      this.itemToDelete = item;
      this.warningModal = true;
    },
    addNewMerchant() {
      this.merchant = {
        name: "",
      };

      this.editMerchantModal = true;
    },
    refreshMerchants() {
      this.merchantLoading = true;
      this.merchantError = false;
      this.merchantApi
        .getListByChartAccountId(this.obj.id)
        .then((response) => {
          this.merchantItems = response.result || [];
        })
        .catch((err) => {
          console.error(err);
          this.merchantError = true;
        })
        .finally(() => {
          this.merchantLoading = false;
        });
    },
    resetObj() {
      if (this.$route.params.id) {
        this.api
          .get(this.$route.params.id)
          .then((response) => {
            this.obj = response.result;
            this.selectedAccountType = this.obj.accountType;
            this.selectedCategory = this.obj.chartAccountGroup || { name: "" };

            this.refreshMerchants();
          })
          .catch((err) => {
            console.error(err);
            this.$toast.error(
              "Couldn't load this account. Go back to the list and try again."
            );
          });
      } else {
        this.obj = this.getEmptyObj();
      }
    },
    onSubmit() {
      this.obj.accountType = this.selectedAccountType;
      this.saving = true;
      const isNew = !this.obj.id;
      const req = isNew ? this.api.create(this.obj) : this.api.update(this.obj);
      req
        .then(() => {
          this.$toast.success(isNew ? "Account added" : "Account saved");
          this.$router.push({ path: "/tenants/chartOfAccountList" });
        })
        .catch(({ data } = {}) => {
          this.$toast.error(
            `Couldn't save the account. ${helper.getErrorMessage(data)}`
          );
        })
        .finally(() => {
          this.saving = false;
        });
    },
    getEmptyObj() {
      return {};
    },
    submit() {
      if (this.saving) return;
      this.onSubmit();
      this.submitted = true;
    },
  },
};
</script>
